import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import ChatbotStore from '@/store/ChatbotStore';
import StationStore from '@/store/Station/StationStore';

interface UseConversationManagementProps {
  chatbotStore: ChatbotStore;
  stationStore: StationStore;
}

const CONVERSATION_STORAGE_KEY = 'ai_assistant_current_conversation';

export const useConversationManagement = ({ chatbotStore, stationStore }: UseConversationManagementProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const hasInitializedRef = useRef<boolean>(false);
  const previousStationIdRef = useRef<string | null>(null);
  const [hasCurrentConversation, setHasCurrentConversation] = useState<boolean>(false);

  const updateConversationUrl = (conversationId: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('conversationId', conversationId);

    // Get the current station ID from the store (source of truth) instead of relying on location.pathname
    // This prevents using a stale pathname when the station has changed but location.pathname hasn't updated yet
    const currentStationId = stationStore?.currentStation?.id;
    const expectedPathname = currentStationId ? `/home/station/${currentStationId}/ai-assistant` : location.pathname;

    navigate(
      {
        pathname: expectedPathname,
        search: newSearchParams.toString(),
      },
      { replace: true },
    );
  };

  const saveConversationToStorage = (conversationId: string) => {
    try {
      localStorage.setItem(CONVERSATION_STORAGE_KEY, conversationId);
    } catch (error) {
      console.error('Failed to save conversation to localStorage:', error);
    }
  };

  const getConversationFromStorage = (): string | null => {
    try {
      return localStorage.getItem(CONVERSATION_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to get conversation from localStorage:', error);
      return null;
    }
  };

  const clearConversationFromStorage = () => {
    try {
      localStorage.removeItem(CONVERSATION_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear conversation from localStorage:', error);
    }
  };

  useEffect(() => {
    const stationId = stationStore?.currentStation?.id;
    if (!stationId || hasInitializedRef.current) {
      return;
    }

    const initializeConversations = async () => {
      await chatbotStore.fetchConversations(stationId);

      const conversationIdFromUrl = searchParams.get('conversationId');
      const conversationIdFromStorage = getConversationFromStorage();

      if (conversationIdFromUrl) {
        chatbotStore.setCurrentConversation(conversationIdFromUrl);
        saveConversationToStorage(conversationIdFromUrl);
        setHasCurrentConversation(true);
      } else if (conversationIdFromStorage) {
        // Check if the stored conversation still exists
        const conversation = chatbotStore.conversationsMap.get(conversationIdFromStorage);
        if (conversation) {
          chatbotStore.setCurrentConversation(conversationIdFromStorage);
          updateConversationUrl(conversationIdFromStorage);
          setHasCurrentConversation(true);
        } else {
          // Stored conversation doesn't exist, clear it
          clearConversationFromStorage();
          setHasCurrentConversation(false);
        }
      } else {
        // No conversation found, show main page
        setHasCurrentConversation(false);
      }

      hasInitializedRef.current = true;
    };

    initializeConversations();
  }, [stationStore?.currentStation?.id, chatbotStore, searchParams, setSearchParams]);

  useEffect(() => {
    if (chatbotStore.currentConversationId && hasInitializedRef.current) {
      const currentConversationId = searchParams.get('conversationId');
      if (currentConversationId !== chatbotStore.currentConversationId) {
        updateConversationUrl(chatbotStore.currentConversationId);
      }
      saveConversationToStorage(chatbotStore.currentConversationId);
      setHasCurrentConversation(true);
    } else if (!chatbotStore.currentConversationId && hasInitializedRef.current) {
      clearConversationFromStorage();
      setHasCurrentConversation(false);
    }
  }, [chatbotStore.currentConversationId, searchParams, setSearchParams]);

  useEffect(() => {
    const currentStationId = stationStore?.currentStation?.id;
    const previousStationId = previousStationIdRef.current;
    const isStationChange = previousStationId !== null && previousStationId !== currentStationId;

    if (currentStationId && chatbotStore && hasInitializedRef.current) {
      if (isStationChange) {
        // When station changes, clear current conversation and show main assistant page
        chatbotStore.setCurrentConversation(null);
        clearConversationFromStorage();
        setHasCurrentConversation(false);
        // Remove conversationId from URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('conversationId');
        setSearchParams(newSearchParams, { replace: true });
        // Fetch conversations for the new station
        chatbotStore.fetchConversations(currentStationId);
      }
      previousStationIdRef.current = currentStationId;
    }
  }, [stationStore?.currentStation?.id, chatbotStore, searchParams, setSearchParams]);

  const ensureConversation = () => {
    if (!chatbotStore.currentConversationId) {
      chatbotStore.createNewConversation();
      if (chatbotStore.currentConversationId) {
        updateConversationUrl(chatbotStore.currentConversationId);
        saveConversationToStorage(chatbotStore.currentConversationId);
        setHasCurrentConversation(true);
      }
    }
  };

  const createNewConversation = () => {
    chatbotStore.createNewConversation();
    if (chatbotStore.currentConversationId) {
      updateConversationUrl(chatbotStore.currentConversationId);
      saveConversationToStorage(chatbotStore.currentConversationId);
      setHasCurrentConversation(true);
    }
  };

  return { ensureConversation, createNewConversation, hasCurrentConversation };
};
